/*
 * Customizer for the Interactive Avatar Panel V2.
 *
 * Everything the panel reads lives in one JSON column (agents.avatar_panel_config),
 * so this screen edits a plain object and saves it in a single PUT. The preview
 * on the right runs the same buildAvatarPanelTheme() the real panel uses, which
 * is what makes "pick a cream background and the whole thing becomes a light
 * theme" visible here rather than only after a save.
 */
import React, { useState, useRef, useMemo } from 'react';
import {
  X, Palette, Eye, Upload, Trash2, Loader2, Layout, Type, SlidersHorizontal,
  Sparkles, Mic, Send, ChevronDown, PhoneOff, MessageSquare,
} from 'lucide-react';
import api from '../api';
import Toast from './Toast';
import { ChatImageSharingSetting } from './chatImageAttachments';
import {
  AVATAR_PANEL_DEFAULTS, buildAvatarPanelTheme, avatarPanelParams, avatarSpotlightSize,
  avatarSpotlightStageHeight,
} from './AvatarPanelV2';

const TABS = [
  { id: 'placement', name: 'Placement', icon: Layout },
  { id: 'colors', name: 'Colors', icon: Palette },
  { id: 'launcher', name: 'Launcher', icon: Sparkles },
  { id: 'content', name: 'Content', icon: Type },
  { id: 'behavior', name: 'Behavior', icon: SlidersHorizontal },
];

// Ready-made palettes. Each one only has to set the four inputs the theme
// builder derives everything else from.
const PRESETS = [
  { name: 'Midnight', bgColor: '#0c1220', surfaceColor: '#161f31', accentColor: '#5b8cff', launcherColor: '#5b8cff', textColor: '' },
  { name: 'Graphite', bgColor: '#141416', surfaceColor: '#1f1f23', accentColor: '#f0b23c', launcherColor: '#f0b23c', textColor: '' },
  { name: 'Navy', bgColor: '#020e1e', surfaceColor: '#0a3161', accentColor: '#769fc8', launcherColor: '#d11242', textColor: '' },
  { name: 'Forest', bgColor: '#0b1a14', surfaceColor: '#14291f', accentColor: '#3ec98a', launcherColor: '#3ec98a', textColor: '' },
  { name: 'Cream', bgColor: '#faf7f2', surfaceColor: '#f0ebe2', accentColor: '#1f4d3d', launcherColor: '#1f4d3d', textColor: '' },
  { name: 'Paper', bgColor: '#ffffff', surfaceColor: '#f4f5f7', accentColor: '#2563eb', launcherColor: '#2563eb', textColor: '' },
];

const Field = ({ label, hint, children }) => (
  <div className="space-y-1.5">
    <label className="text-xs font-semibold text-foreground">{label}</label>
    {children}
    {hint && <p className="text-[11px] text-muted-foreground leading-relaxed">{hint}</p>}
  </div>
);

const TextInput = (props) => (
  <input
    type="text"
    {...props}
    className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm"
  />
);

const NumberInput = ({ value, onChange, min, max, suffix }) => (
  <div className="flex items-center gap-2">
    <input
      type="number"
      value={value}
      min={min}
      max={max}
      onChange={(e) => {
        const n = parseInt(e.target.value, 10);
        onChange(Number.isFinite(n) ? Math.min(max, Math.max(min, n)) : min);
      }}
      className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm"
    />
    {suffix && <span className="text-xs text-muted-foreground flex-shrink-0">{suffix}</span>}
  </div>
);

const Slider = ({ value, onChange, min, max, step = 1, format }) => (
  <div className="flex items-center gap-3">
    <input
      type="range"
      value={value}
      min={min}
      max={max}
      step={step}
      onChange={(e) => onChange(parseInt(e.target.value, 10))}
      className="flex-1 accent-primary"
    />
    <span className="text-xs font-semibold text-foreground tabular-nums w-14 text-right flex-shrink-0">
      {format ? format(value) : value}
    </span>
  </div>
);

// Four sizes per layout, and nobody should ever have to type a number to get one
// of them.
//
// The two layouts want genuinely different cards, which is why this is two lists
// and not one. Side by side needs width: two columns, and every width here is
// 680 or more because that is where the card still has room for two readable
// ones. Video on top needs height instead - it is a video band with a
// conversation under it, so a wide card just makes the band a letterbox slit.
// The stacked sizes are all shaped to keep that band under 2:1 at the default
// 45% video height, which is where cropping starts taking the top of the head.
const SPOTLIGHT_PRESETS = {
  split: [
    { name: 'Small', width: 700, height: 500 },
    { name: 'Medium', width: 780, height: 560 },
    { name: 'Standard', width: 860, height: 620 },
    { name: 'Large', width: 1040, height: 680 },
  ],
  stacked: [
    { name: 'Small', width: 480, height: 560 },
    { name: 'Medium', width: 540, height: 620 },
    { name: 'Standard', width: 600, height: 680 },
    { name: 'Large', width: 680, height: 740 },
  ],
};

const presetsFor = (layout) => (layout === 'stacked' ? SPOTLIGHT_PRESETS.stacked : SPOTLIGHT_PRESETS.split);

const matchesPreset = (config) => presetsFor(config.spotlightLayout).find(
  (preset) => preset.width === config.spotlightWidth && preset.height === config.spotlightHeight
);

// A hand-typed size switching layouts. The one thing the client clearly meant by
// those two numbers is how much of the page the card takes, so the area is kept
// and only the shape changes - to whatever the new layout's Standard is.
const reshapeTo = (width, height, target) => {
  const area = Math.max(1, width * height);
  const aspect = target.width / target.height;
  const w = Math.min(1200, Math.max(420, Math.round(Math.sqrt(area * aspect))));
  const h = Math.min(900, Math.max(420, Math.round(w / aspect)));
  return { spotlightWidth: w, spotlightHeight: h };
};

const Segmented = ({ value, options, onChange, columns = 2 }) => (
  <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}>
    {options.map((opt) => (
      <button
        key={opt.value}
        type="button"
        onClick={() => onChange(opt.value)}
        className={`px-3 py-2 rounded-lg border-2 text-xs transition-all ${
          value === opt.value
            ? 'border-primary bg-primary/10 text-primary font-semibold'
            : 'border-border hover:border-primary/50 text-foreground'
        }`}
      >
        {opt.name}
      </button>
    ))}
  </div>
);

const ColorPicker = ({ label, field, value, fallback, onChange, hint }) => {
  const shown = value || fallback || '#000000';
  return (
    <Field label={label} hint={hint}>
      <div className="bg-muted/30 border border-border/60 rounded-xl p-2.5 flex items-center gap-2.5">
        <label className="relative cursor-pointer flex-shrink-0">
          <div className="w-10 h-8 rounded-lg border-2 border-white shadow-md" style={{ backgroundColor: shown }} />
          <input
            type="color"
            value={shown}
            onChange={(e) => onChange(field, e.target.value)}
            className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
          />
        </label>
        <input
          type="text"
          value={(value || '').replace('#', '')}
          placeholder={(fallback || '').replace('#', '') || 'auto'}
          onChange={(e) => {
            const raw = e.target.value.replace('#', '');
            if (/^[0-9A-Fa-f]{0,6}$/.test(raw)) onChange(field, raw ? `#${raw}` : '');
          }}
          className="flex-1 min-w-0 px-2.5 py-1.5 text-xs font-mono bg-background border-2 border-border rounded-lg uppercase"
          maxLength={6}
        />
      </div>
    </Field>
  );
};

const Toggle = ({ checked, onChange, label }) => (
  <button type="button" onClick={onChange} className="flex items-center gap-3 text-left w-full">
    <span
      role="switch"
      aria-checked={checked}
      className={`relative inline-flex h-5 w-9 flex-shrink-0 rounded-full transition-colors ${
        checked ? 'bg-primary' : 'bg-muted'
      }`}
    >
      <span
        className={`inline-block h-4 w-4 mt-0.5 ml-0.5 transform rounded-full bg-white shadow transition ${
          checked ? 'translate-x-4' : 'translate-x-0'
        }`}
      />
    </span>
    <span className="text-xs text-muted-foreground">{label}</span>
  </button>
);

// How much of a real screen the spotlight would actually take. "860 x 620"
// means nothing on its own - the number that matters is the share of the page
// it hides, and it is different on every monitor. Three common sizes, measured
// with the same function the panel uses, so this cannot drift from it.
const REFERENCE_SCREENS = [
  { name: 'Laptop', vw: 1440, vh: 810 },
  { name: 'Desktop', vw: 1920, vh: 1080 },
];

// The slider asks for a share of the height; the panel gives the conversation a
// 180px floor first. On a short card those two disagree, so this prints what the
// panel will actually do rather than leaving the slider to lie about it.
const StackedSplitReadout = ({ settings }) => {
  const rows = useMemo(() => {
    const cardH = avatarSpotlightSize(settings, 1440, 810).height;
    const stage = avatarSpotlightStageHeight(settings, cardH);
    return { cardH, stage, body: cardH - stage - 62 };
  }, [settings]);
  const asked = settings.spotlightStageRatio;
  const actual = Math.round((rows.stage / rows.cardH) * 100);
  return (
    <p className="text-[11px] text-muted-foreground">
      On a {rows.cardH}px card: {rows.stage}px of video, {rows.body}px of conversation.
      {actual < asked - 1 && (
        <span className="text-amber-600">
          {' '}The card is too short for {asked}%, so the video takes {actual}%.
        </span>
      )}
    </p>
  );
};

const ScreenCoverage = ({ settings }) => {
  const screens = useMemo(() => {
    const list = REFERENCE_SCREENS.slice();
    if (typeof window !== 'undefined' && window.innerWidth >= 900) {
      list.push({ name: 'This screen', vw: window.innerWidth, vh: window.innerHeight });
    }
    return list.map((screen) => {
      const box = avatarSpotlightSize(settings, screen.vw, screen.vh);
      return {
        ...screen,
        ...box,
        coverage: Math.round((box.width * box.height) / (screen.vw * screen.vh) * 100),
      };
    });
  }, [settings]);

  return (
    <div className="rounded-xl border border-border bg-muted/30 p-3 space-y-1.5">
      <div className="text-[11px] font-semibold text-foreground">Screen it covers</div>
      {screens.map((screen) => (
        <div key={screen.name} className="flex items-center justify-between gap-3 text-[11px]">
          <span className="text-muted-foreground">{screen.name}</span>
          <span className="tabular-nums text-foreground">
            {screen.width} x {screen.height}
            <span className={screen.coverage > 55 ? 'text-amber-600 ml-2' : 'text-muted-foreground ml-2'}>
              {screen.coverage}% of the page
            </span>
          </span>
        </div>
      ))}
    </div>
  );
};

// ─── Live preview ─────────────────────────────────────────────────────────────
// A still of the real panel: same derived theme, same proportions. It is not
// the live component - that needs an avatar session - but every colour, radius
// and piece of copy on screen here is the one the visitor will see.
const Preview = ({ settings, theme }) => {
  const isSpotlight = settings.variant === 'spotlight';
  // The spotlight preview is drawn to scale against a 1440px screen, so moving
  // the size slider moves this card the same way it moves the real one.
  const spotlightPct = useMemo(() => {
    if (!isSpotlight) return 100;
    const box = avatarSpotlightSize(settings, 1440, 810);
    return Math.round((box.width / 1440) * 100);
  }, [isSpotlight, settings]);
  // Which of the two shapes the card is in, and - when it is stacked - how much
  // of it the video really gets once the conversation's floor is taken out.
  const previewSplit = isSpotlight
    && settings.spotlightLayout !== 'stacked'
    && !settings.hideAvatar;
  const stageHeight = useMemo(() => {
    if (!isSpotlight || settings.spotlightLayout !== 'stacked') return isSpotlight ? 108 : 96;
    const cardH = avatarSpotlightSize(settings, 1440, 810).height;
    return Math.round(240 * (avatarSpotlightStageHeight(settings, cardH) / cardH));
  }, [isSpotlight, settings]);
  const anchor = settings.spotlightAnchor || 'center';
  const align = !isSpotlight || anchor === 'center' || anchor === 'bottom' || anchor === 'top'
    ? 'items-center'
    : anchor === 'bottom-left' ? 'items-start' : 'items-end';
  const justify = !isSpotlight || anchor === 'center'
    ? 'justify-center'
    : anchor === 'top' ? 'justify-start' : 'justify-end';
  return (
    <div
      className="rounded-2xl overflow-hidden border border-border relative"
      style={{
        background:
          'repeating-conic-gradient(rgba(125,125,125,.10) 0% 25%, transparent 0% 50%) 50% / 18px 18px',
        minHeight: 430,
      }}
    >
      <div className={`absolute inset-0 p-5 flex flex-col gap-4 ${align} ${justify}`}>
        {/* Panel */}
        <div
          style={{
            width: isSpotlight ? `${spotlightPct}%` : 260,
            minWidth: 200,
            maxWidth: '100%',
            background: theme.bg,
            color: theme.text,
            border: `1px solid ${theme.border}`,
            borderRadius: theme.radius,
            boxShadow: theme.shadow,
            overflow: 'hidden',
            fontFamily: theme.font,
          }}
        >
          <div
            className="flex items-center gap-2.5 px-3"
            style={{ height: 50, borderBottom: `1px solid ${theme.hairline}` }}
          >
            <div
              className="flex items-center justify-center flex-shrink-0 overflow-hidden"
              style={{ width: 28, height: 28, borderRadius: 9, background: settings.logoUrl ? 'transparent' : theme.accentSoft }}
            >
              {settings.logoUrl
                ? <img src={settings.logoUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                : <Sparkles size={14} style={{ color: theme.accent }} />}
            </div>
            <div className="min-w-0 flex-1">
              <div style={{ fontSize: 12, fontWeight: 650, letterSpacing: '-0.015em' }} className="truncate">
                {settings.title || 'Assistant'}
              </div>
              <div style={{ fontSize: 9.5, color: theme.muted }} className="truncate">
                {settings.subtitle}
              </div>
            </div>
            <ChevronDown size={14} style={{ color: theme.muted }} />
            <X size={13} style={{ color: theme.muted }} />
          </div>

          <div className={`p-2.5 ${previewSplit ? 'flex gap-2.5' : 'space-y-2.5'}`}>
            {!settings.hideAvatar && (
            <div
              style={{
                // Split: a full-height column beside the conversation. Stacked:
                // a band whose height tracks the video-height slider.
                ...(previewSplit
                  ? { width: '46%', flexShrink: 0, alignSelf: 'stretch' }
                  : { height: stageHeight }),
                borderRadius: 14,
                background: settings.posterUrl
                  ? `url(${settings.posterUrl}) center/cover`
                  : `radial-gradient(120% 90% at 50% 0%, ${theme.accentSoft} 0%, transparent 62%), ${theme.elevated}`,
                display: 'grid',
                placeItems: 'center',
                position: 'relative',
                boxShadow: `inset 0 0 0 2px ${theme.accent}55`,
              }}
            >
              <span
                className="absolute flex items-center gap-1"
                style={{
                  top: 7, left: 8, padding: '3px 7px', borderRadius: 99,
                  background: 'rgba(8,12,20,.55)', color: '#fff',
                  fontSize: 8, fontWeight: 700, letterSpacing: '.06em',
                }}
              >
                <span style={{ width: 4, height: 4, borderRadius: 99, background: theme.live }} />
                LIVE 0:42
              </span>
              {!settings.posterUrl && <Sparkles size={22} style={{ color: theme.accent, opacity: 0.55 }} />}
            </div>
            )}

            <div
              className={previewSplit ? 'flex-1 min-w-0 flex flex-col gap-2.5' : 'space-y-2.5'}
              style={previewSplit ? { minHeight: 190 } : null}
            >
            <div
              className="space-y-1.5"
              // The transcript inherits the height the video was using, so the
              // preview keeps the panel's proportions either way.
              style={{
                paddingInline: 2,
                ...(previewSplit ? { flex: 1 } : null),
                ...(settings.hideAvatar ? { minHeight: isSpotlight ? 108 : 96 } : null),
              }}
            >
              <div className="flex justify-start">
                <div
                  style={{
                    maxWidth: '84%', padding: '6px 10px', fontSize: 10.5, lineHeight: 1.45,
                    borderRadius: 13, borderBottomLeftRadius: 4,
                    background: theme.surface, border: `1px solid ${theme.hairline}`, color: theme.text,
                  }}
                >
                  {settings.headline ? `${settings.headline}. How can I help?` : 'How can I help?'}
                </div>
              </div>
              <div className="flex justify-end">
                <div
                  style={{
                    maxWidth: '84%', padding: '6px 10px', fontSize: 10.5, lineHeight: 1.45,
                    borderRadius: 13, borderBottomRightRadius: 4,
                    background: theme.accent, color: theme.accentText,
                  }}
                >
                  I can also just type instead.
                </div>
              </div>
            </div>

            <div className="flex items-center gap-1.5 pt-1">
              {settings.showMicToggle && (
                <div
                  className="grid place-items-center flex-shrink-0"
                  style={{ width: 30, height: 30, borderRadius: 10, background: theme.surface, color: theme.text }}
                >
                  <Mic size={13} />
                </div>
              )}
              {settings.showTextInput && (
                <div
                  className="flex-1 flex items-center justify-between px-2.5"
                  style={{
                    height: 30, borderRadius: 10, background: theme.surface,
                    border: `1px solid ${theme.hairline}`, fontSize: 10, color: theme.faint,
                  }}
                >
                  <span className="truncate">{settings.inputPlaceholder}</span>
                  <Send size={11} style={{ color: theme.accent }} />
                </div>
              )}
              <div
                className="grid place-items-center flex-shrink-0"
                style={{ width: 30, height: 30, borderRadius: 10, background: theme.dangerSoft, color: theme.danger }}
              >
                <PhoneOff size={13} />
              </div>
            </div>
            {settings.poweredBy && (
              <div style={{ textAlign: 'center', fontSize: 8, color: theme.faint }}>Powered by BrainCX</div>
            )}
            </div>
          </div>
        </div>

        {/* Launcher */}
        <div
          className="flex w-full"
          style={{ justifyContent: settings.position.endsWith('left') ? 'flex-start' : 'flex-end' }}
        >
          <div
            className="inline-flex items-center justify-center"
            style={{
              gap: 8,
              height: settings.launcherStyle === 'circle' ? 52 : 48,
              width: settings.launcherStyle === 'circle' ? 52 : 'auto',
              padding: settings.launcherStyle === 'circle' ? 0 : '0 16px 0 5px',
              borderRadius: 999,
              background: theme.launcher,
              color: theme.launcherText,
              fontSize: 13,
              fontWeight: 600,
              boxShadow: `0 10px 26px ${theme.launcher}55`,
            }}
          >
            <span
              className="grid place-items-center flex-shrink-0 overflow-hidden"
              style={{
                width: settings.launcherStyle === 'circle' ? '100%' : 38,
                height: settings.launcherStyle === 'circle' ? '100%' : 38,
                borderRadius: '50%',
                background: settings.launcherStyle === 'circle' ? 'transparent' : `${theme.launcherText}29`,
              }}
            >
              {settings.showLauncherPreview && (settings.logoUrl || (!settings.hideAvatar && settings.posterUrl)) ? (
                <img
                  src={settings.hideAvatar ? settings.logoUrl : (settings.posterUrl || settings.logoUrl)}
                  alt=""
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              ) : settings.launcherIcon === 'chat' ? <MessageSquare size={17} />
                : settings.launcherIcon === 'mic' ? <Mic size={17} />
                : <Sparkles size={17} />}
            </span>
            {settings.launcherStyle !== 'circle' && (
              <span style={{ whiteSpace: 'nowrap' }}>{settings.launcherLabel}</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Customizer ───────────────────────────────────────────────────────────────

const AvatarPanelThemeCustomizer = ({ agent, onClose, onSave }) => {
  const [tab, setTab] = useState('placement');
  const [settings, setSettings] = useState(() => ({
    ...AVATAR_PANEL_DEFAULTS,
    ...(agent?.avatar_panel_config || {}),
    title: agent?.avatar_panel_config?.title || agent?.name || '',
  }));
  // The size numbers stay folded away unless they are already in use. Someone
  // who picked a preset never has to look at a pixel value; someone who typed
  // one has it in front of them when they open the tab.
  const [showCustomSize, setShowCustomSize] = useState(() => {
    const cfg = { ...AVATAR_PANEL_DEFAULTS, ...(agent?.avatar_panel_config || {}) };
    return cfg.spotlightSizeMode === 'scale' || !matchesPreset(cfg);
  });
  const [logoUrl, setLogoUrl] = useState(agent?.avatar_panel_logo_url || null);
  // An agent column rather than part of avatar_panel_config: the same switch
  // governs the Chat widget and Combo Widget too.
  const [imageSharing, setImageSharing] = useState(Boolean(agent?.chat_image_upload_enabled));
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [toast, setToast] = useState(null);
  const fileInputRef = useRef(null);

  const set = (field, value) => setSettings((prev) => ({ ...prev, [field]: value }));

  // Switching layout resizes the card with it. A card sized for two columns is
  // the wrong card for a video band and the other way round, and leaving the old
  // numbers behind is how someone flips the option, sees something worse, and
  // decides the option is broken. A preset keeps its name across the switch; a
  // hand-typed size keeps its area and takes the new shape.
  const changeLayout = (nextLayout) => setSettings((prev) => {
    if (nextLayout === prev.spotlightLayout) return prev;
    // No stage to lay out, so there is nothing to resize for.
    if (prev.hideAvatar) return { ...prev, spotlightLayout: nextLayout };
    const next = presetsFor(nextLayout);
    const standard = next.find((p) => p.name === 'Standard') || next[0];
    const current = matchesPreset(prev);
    const twin = current && next.find((p) => p.name === current.name);
    return {
      ...prev,
      spotlightLayout: nextLayout,
      ...(twin
        ? { spotlightWidth: twin.width, spotlightHeight: twin.height }
        : reshapeTo(prev.spotlightWidth, prev.spotlightHeight, standard)),
    };
  });
  const toggle = (field) => setSettings((prev) => ({ ...prev, [field]: !prev[field] }));

  const previewSettings = useMemo(() => ({ ...settings, logoUrl }), [settings, logoUrl]);
  const theme = useMemo(() => buildAvatarPanelTheme(previewSettings), [previewSettings]);

  const applyPreset = (preset) =>
    setSettings((prev) => ({
      ...prev,
      bgColor: preset.bgColor,
      surfaceColor: preset.surfaceColor,
      accentColor: preset.accentColor,
      launcherColor: preset.launcherColor,
      textColor: preset.textColor,
    }));

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const payload = { ...settings };
      delete payload.logoUrl; // logo lives in its own column, written by the upload endpoint
      await api.put(`/agents/${agent.id}`, { avatar_panel_config: payload, chat_image_upload_enabled: imageSharing });
      setToast({ message: 'Avatar panel settings saved.', type: 'success' });
      setTimeout(() => {
        onSave?.({ avatar_panel_config: payload, avatar_panel_logo_url: logoUrl, chat_image_upload_enabled: imageSharing });
        onClose();
      }, 700);
    } catch (err) {
      setToast({ message: err.response?.data?.detail || 'Failed to save settings', type: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/\.(jpg|jpeg|png|webp|gif|svg)$/i.test(file.name)) {
      setUploadError('Please upload a JPG, PNG, WebP, GIF, or SVG.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setUploadError('File too large. Maximum size is 5MB.');
      return;
    }
    setIsUploading(true);
    setUploadError(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post(`/agents/${agent.id}/avatar-panel/logo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setLogoUrl(response.data.file_url);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (err) {
      setUploadError(err.response?.data?.detail || 'Failed to upload logo');
    } finally {
      setIsUploading(false);
    }
  };

  const handleLogoDelete = async () => {
    try {
      await api.delete(`/agents/${agent.id}/avatar-panel/logo`);
      setLogoUrl(null);
    } catch (err) {
      setUploadError(err.response?.data?.detail || 'Failed to delete logo');
    }
  };

  // Preview runs against the demo host page so the panel is seen the way a
  // visitor sees it: over real content, with the page still scrollable.
  const handlePreview = () => {
    const params = avatarPanelParams(settings);
    params.set('agent', agent.id);
    window.open(`${window.location.origin}/avatar-panel-v2-demo.html?${params}`, '_blank');
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[10000] flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-2xl max-w-6xl w-full max-h-[92vh] overflow-hidden flex flex-col shadow-elegant">
        <div className="bg-gradient-secondary text-white p-5 flex justify-between items-center flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-7 h-7 rounded-lg bg-white/20 flex items-center justify-center">
              <Palette className="w-4 h-4" />
            </div>
            <div>
              <h2 className="text-lg font-semibold">Customize Interactive Avatar Panel</h2>
              <p className="text-white/70 text-xs mt-0.5">
                Colors, copy, launcher and placement. The panel never dims the page behind it.
              </p>
            </div>
          </div>
          <button type="button" onClick={onClose} className="text-white/80 hover:text-white p-1.5 rounded-lg hover:bg-white/10">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-[1fr_380px]">
          {/* Settings */}
          <div className="min-h-0 flex flex-col border-r border-border">
            <div className="flex gap-1 px-4 pt-3 pb-2 border-b border-border overflow-x-auto flex-shrink-0">
              {TABS.map(({ id, name, icon: Icon }) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => setTab(id)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1.5 whitespace-nowrap transition-colors ${
                    tab === id ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted'
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {name}
                </button>
              ))}
            </div>

            <div className="flex-1 overflow-y-auto p-5 space-y-5">
              {tab === 'placement' && (
                <>
                  <Field
                    label="Pattern"
                    hint="Drawer opens a full-height panel at the side of the page. Spotlight grows a bottom command pill into a centered card. Neither one dims or blurs the page."
                  >
                    <Segmented
                      value={settings.variant}
                      onChange={(v) => set('variant', v)}
                      options={[
                        { value: 'drawer', name: 'Side drawer' },
                        { value: 'spotlight', name: 'Spotlight' },
                      ]}
                    />
                  </Field>

                  {settings.variant === 'drawer' ? (
                    <>
                      <Field label="Drawer side">
                        <Segmented
                          value={settings.side}
                          onChange={(v) => set('side', v)}
                          options={[{ value: 'right', name: 'Right' }, { value: 'left', name: 'Left' }]}
                        />
                      </Field>
                      <Field label="Drawer edge" hint="Floating leaves a 16px gap and rounds all four corners. Flush sits against the browser edge, full bleed.">
                        <Segmented
                          value={settings.drawerEdge}
                          onChange={(v) => set('drawerEdge', v)}
                          options={[{ value: 'floating', name: 'Floating' }, { value: 'flush', name: 'Flush' }]}
                        />
                      </Field>
                      <Field label="Drawer width">
                        <NumberInput value={settings.panelWidth} min={320} max={620} onChange={(v) => set('panelWidth', v)} suffix="px" />
                      </Field>
                    </>
                  ) : (
                    <>
                      <Field
                        label="Layout"
                        hint="Side by side puts the avatar in a full-height column with the conversation beside it. Video on top is the shape of a video call: the avatar across the card, the conversation under it. Switching resizes the card to suit, since the two shapes need different proportions."
                      >
                        <Segmented
                          value={settings.spotlightLayout}
                          onChange={changeLayout}
                          options={[
                            { value: 'split', name: 'Side by side' },
                            { value: 'stacked', name: 'Video on top' },
                          ]}
                        />
                      </Field>

                      {settings.spotlightLayout === 'stacked' && !settings.hideAvatar && (
                        <Field label="Video height">
                          <Slider
                            value={settings.spotlightStageRatio}
                            min={25}
                            max={60}
                            onChange={(v) => set('spotlightStageRatio', v)}
                            format={(v) => `${v}%`}
                          />
                          <StackedSplitReadout settings={settings} />
                        </Field>
                      )}

                      {settings.spotlightLayout === 'stacked' && settings.hideAvatar && (
                        <p className="text-[11px] text-muted-foreground -mt-1">
                          The avatar is switched off in the Behavior tab, so the conversation already
                          fills the card and this layout changes nothing.
                        </p>
                      )}

                      <Field
                        label="Spotlight size"
                        hint="Pick one. The percentage is how much of a 1440px laptop screen the card covers, which is the number that actually matters."
                      >
                        <div className="grid grid-cols-2 gap-2">
                          {presetsFor(settings.spotlightLayout).map((preset) => {
                            const active = settings.spotlightSizeMode !== 'scale'
                              && settings.spotlightWidth === preset.width
                              && settings.spotlightHeight === preset.height;
                            return (
                              <button
                                key={preset.name}
                                type="button"
                                title={preset.note}
                                onClick={() => setSettings((prev) => ({
                                  ...prev,
                                  spotlightSizeMode: 'fixed',
                                  spotlightWidth: preset.width,
                                  spotlightHeight: preset.height,
                                }))}
                                className={`px-3 py-2.5 rounded-lg border-2 text-left transition-all ${
                                  active
                                    ? 'border-primary bg-primary/10'
                                    : 'border-border hover:border-primary/50'
                                }`}
                              >
                                <span className={`block text-xs ${active ? 'text-primary font-semibold' : 'text-foreground font-medium'}`}>
                                  {preset.name}
                                </span>
                                <span className="block text-[10px] text-muted-foreground tabular-nums mt-0.5">
                                  {preset.width} x {preset.height}
                                  {' · '}
                                  {Math.round((preset.width * preset.height) / (1440 * 810) * 100)}% of the screen
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      </Field>

                      <button
                        type="button"
                        onClick={() => setShowCustomSize((v) => !v)}
                        className="text-[11px] font-medium text-muted-foreground hover:text-foreground flex items-center gap-1"
                      >
                        <ChevronDown
                          className={`w-3 h-3 transition-transform ${showCustomSize ? '' : '-rotate-90'}`}
                        />
                        {showCustomSize ? 'Hide' : 'Set my own size'}
                        {!showCustomSize && !matchesPreset(settings) && (
                          <span className="text-primary font-semibold">
                            {settings.spotlightSizeMode === 'scale'
                              ? `(scaling to ${settings.spotlightScale}%)`
                              : `(${settings.spotlightWidth} x ${settings.spotlightHeight})`}
                          </span>
                        )}
                      </button>

                      {showCustomSize && (
                        <div className="rounded-xl border border-border bg-muted/20 p-3 space-y-4">
                          <Field
                            label="How it is sized"
                            hint="Fixed keeps the same pixel size on every screen, so a card sized for a large monitor covers most of a laptop. Scale sizes it as a share of the visitor's own screen instead."
                          >
                            <Segmented
                              value={settings.spotlightSizeMode}
                              onChange={(v) => set('spotlightSizeMode', v)}
                              options={[
                                { value: 'fixed', name: 'Fixed size' },
                                { value: 'scale', name: 'Scale to screen' },
                              ]}
                            />
                          </Field>

                          {settings.spotlightSizeMode === 'scale' ? (
                            <>
                              <Field
                                label="Share of the screen"
                                hint="Applied to both the width and the height, so the card keeps the shape of the screen it is on."
                              >
                                <Slider
                                  value={settings.spotlightScale}
                                  min={30}
                                  max={95}
                                  onChange={(v) => set('spotlightScale', v)}
                                  format={(v) => `${v}%`}
                                />
                              </Field>
                              <div className="grid grid-cols-2 gap-3">
                                <Field label="Never wider than">
                                  <NumberInput value={settings.spotlightWidth} min={420} max={1200} onChange={(v) => set('spotlightWidth', v)} suffix="px" />
                                </Field>
                                <Field label="Never taller than">
                                  <NumberInput value={settings.spotlightHeight} min={420} max={900} onChange={(v) => set('spotlightHeight', v)} suffix="px" />
                                </Field>
                              </div>
                            </>
                          ) : (
                            <>
                              <div className="grid grid-cols-2 gap-3">
                                <Field label="Spotlight width">
                                  <NumberInput value={settings.spotlightWidth} min={420} max={1200} onChange={(v) => set('spotlightWidth', v)} suffix="px" />
                                </Field>
                                <Field label="Spotlight height">
                                  <NumberInput value={settings.spotlightHeight} min={420} max={900} onChange={(v) => set('spotlightHeight', v)} suffix="px" />
                                </Field>
                              </div>
                              {settings.spotlightWidth < 680 && settings.spotlightLayout !== 'stacked' && (
                                <p className="text-[11px] text-amber-600">
                                  Under 680px wide there is no room for two columns, so the card stacks
                                  the video above the conversation whatever the Layout below says.
                                </p>
                              )}
                            </>
                          )}
                        </div>
                      )}

                      <ScreenCoverage settings={settings} />

                      <Field
                        label="Where it sits"
                        hint="Centered puts the card over the middle of the page. Anchoring it to an edge leaves the visitor's place on the page uncovered without making the card smaller."
                      >
                        <Segmented
                          value={settings.spotlightAnchor}
                          onChange={(v) => set('spotlightAnchor', v)}
                          columns={3}
                          options={[
                            { value: 'center', name: 'Centered' },
                            { value: 'bottom', name: 'Bottom' },
                            { value: 'top', name: 'Top' },
                            { value: 'bottom-right', name: 'Bottom right' },
                            { value: 'bottom-left', name: 'Bottom left' },
                          ]}
                        />
                      </Field>


                    </>
                  )}

                  <Field label="Launcher corner">
                    <Segmented
                      value={settings.position}
                      onChange={(v) => set('position', v)}
                      options={[
                        { value: 'bottom-right', name: 'Bottom right' },
                        { value: 'bottom-left', name: 'Bottom left' },
                        { value: 'bottom-center', name: 'Bottom center' },
                        { value: 'top-right', name: 'Top right' },
                      ]}
                    />
                  </Field>

                  <Field
                    label="On phones"
                    hint="Sheet keeps the top of the page visible and scrollable, so the visitor can still browse. Full takes the whole screen. Below 560px wide the spotlight's size, layout and position do not apply: the card is always a full-width sheet."
                  >
                    <Segmented
                      value={settings.mobileMode}
                      onChange={(v) => set('mobileMode', v)}
                      options={[{ value: 'sheet', name: 'Bottom sheet' }, { value: 'full', name: 'Full screen' }]}
                    />
                  </Field>

                  <Field label="Corner radius">
                    <NumberInput value={settings.radius} min={0} max={32} onChange={(v) => set('radius', v)} suffix="px" />
                  </Field>
                </>
              )}

              {tab === 'colors' && (
                <>
                  <Field label="Presets" hint="A starting point. Every colour below can still be set by hand.">
                    <div className="grid grid-cols-3 gap-2">
                      {PRESETS.map((preset) => (
                        <button
                          key={preset.name}
                          type="button"
                          onClick={() => applyPreset(preset)}
                          className="rounded-xl border-2 border-border hover:border-primary/60 p-2 flex flex-col gap-1.5 transition-colors"
                        >
                          <span className="flex gap-1">
                            {[preset.bgColor, preset.surfaceColor, preset.accentColor, preset.launcherColor].map((c, i) => (
                              <span key={i} className="w-4 h-4 rounded-md border border-black/10" style={{ background: c }} />
                            ))}
                          </span>
                          <span className="text-[11px] font-medium text-foreground">{preset.name}</span>
                        </button>
                      ))}
                    </div>
                  </Field>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <ColorPicker
                      label="Panel background"
                      field="bgColor"
                      value={settings.bgColor}
                      onChange={set}
                      hint="Text, borders and muted tones are derived from this. A light value switches the whole panel to a light theme."
                    />
                    <ColorPicker label="Surface (bubbles, inputs)" field="surfaceColor" value={settings.surfaceColor} onChange={set} />
                    <ColorPicker label="Accent (buttons, your bubbles)" field="accentColor" value={settings.accentColor} onChange={set} />
                    <ColorPicker
                      label="Launcher"
                      field="launcherColor"
                      value={settings.launcherColor}
                      fallback={settings.accentColor}
                      onChange={set}
                      hint="Leave empty to reuse the accent."
                    />
                    <ColorPicker
                      label="Text override"
                      field="textColor"
                      value={settings.textColor}
                      fallback={theme.text}
                      onChange={set}
                      hint="Leave empty to let the background decide."
                    />
                  </div>

                  <Field label="Font family" hint="Any family already loaded on the client's site, for example Inter or Söhne. Falls back to the system stack.">
                    <TextInput
                      value={settings.fontFamily}
                      placeholder="System default"
                      onChange={(e) => set('fontFamily', e.target.value)}
                    />
                  </Field>
                </>
              )}

              {tab === 'launcher' && (
                <>
                  <Field label="Brand logo" hint="Shown on the launcher, in the panel header and beside every agent message. JPG, PNG, WebP, GIF or SVG up to 5MB.">
                    <div className="flex items-center gap-3">
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/webp,image/gif,image/svg+xml"
                        onChange={handleLogoUpload}
                        className="hidden"
                        id="avatar-panel-logo-upload"
                      />
                      <label
                        htmlFor="avatar-panel-logo-upload"
                        className="px-3 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg flex items-center gap-2 text-xs cursor-pointer"
                      >
                        {isUploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                        {isUploading ? 'Uploading…' : 'Upload logo'}
                      </label>
                      {logoUrl && (
                        <button
                          type="button"
                          onClick={handleLogoDelete}
                          className="px-3 py-2 text-red-500 hover:bg-red-500/10 rounded-lg flex items-center gap-1.5 text-xs"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                          Remove
                        </button>
                      )}
                    </div>
                    {uploadError && <p className="text-[11px] text-red-500 mt-1">{uploadError}</p>}
                  </Field>

                  <Field label="Avatar still image (URL)" hint="A frame of the avatar, used on the launcher and behind the stage before the session connects. Makes the open feel instant.">
                    <TextInput
                      value={settings.posterUrl}
                      placeholder="https://…/avatar-still.jpg"
                      onChange={(e) => set('posterUrl', e.target.value)}
                    />
                  </Field>

                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Shape">
                      <Segmented
                        value={settings.launcherStyle}
                        onChange={(v) => set('launcherStyle', v)}
                        options={[{ value: 'pill', name: 'Pill' }, { value: 'circle', name: 'Circle' }]}
                      />
                    </Field>
                    <Field label="Size">
                      <Segmented
                        columns={3}
                        value={settings.launcherSize}
                        onChange={(v) => set('launcherSize', v)}
                        options={[
                          { value: 'small', name: 'S' },
                          { value: 'medium', name: 'M' },
                          { value: 'large', name: 'L' },
                        ]}
                      />
                    </Field>
                  </div>

                  <Field label="Launcher label">
                    <TextInput value={settings.launcherLabel} onChange={(e) => set('launcherLabel', e.target.value)} />
                  </Field>

                  <Field label="Fallback icon" hint="Used when there is no logo or still image.">
                    <Segmented
                      columns={3}
                      value={settings.launcherIcon}
                      onChange={(v) => set('launcherIcon', v)}
                      options={[
                        { value: 'sparkles', name: 'Spark' },
                        { value: 'chat', name: 'Chat' },
                        { value: 'mic', name: 'Mic' },
                      ]}
                    />
                  </Field>

                  <div className="space-y-3 bg-muted/20 rounded-xl p-3.5 border border-border">
                    <Toggle
                      checked={settings.showLauncherPreview}
                      onChange={() => toggle('showLauncherPreview')}
                      label={settings.hideAvatar ? 'Show the logo on the launcher' : 'Show the avatar still on the launcher'}
                    />
                    <Toggle checked={settings.pulse} onChange={() => toggle('pulse')} label="Pulse ring around the launcher" />
                    <Toggle checked={settings.teaserEnabled} onChange={() => toggle('teaserEnabled')} label="Proactive greeting bubble (once per tab)" />
                  </div>

                  {settings.teaserEnabled && (
                    <>
                      <Field label="Greeting bubble text">
                        <TextInput value={settings.teaserMessage} onChange={(e) => set('teaserMessage', e.target.value)} />
                      </Field>
                      <Field label="Show it after">
                        <NumberInput value={settings.teaserDelay} min={0} max={60000} onChange={(v) => set('teaserDelay', v)} suffix="ms" />
                      </Field>
                    </>
                  )}
                </>
              )}

              {tab === 'content' && (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Field label="Panel title" hint="Defaults to the agent name.">
                      <TextInput value={settings.title} onChange={(e) => set('title', e.target.value)} />
                    </Field>
                    <Field label="Status line">
                      <TextInput value={settings.subtitle} onChange={(e) => set('subtitle', e.target.value)} />
                    </Field>
                  </div>
                  <Field label="Headline (before the conversation starts)">
                    <TextInput value={settings.headline} onChange={(e) => set('headline', e.target.value)} />
                  </Field>
                  <Field label="Supporting line">
                    <textarea
                      value={settings.description}
                      onChange={(e) => set('description', e.target.value)}
                      rows={2}
                      className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm resize-none"
                    />
                  </Field>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Field label="Start button">
                      <TextInput value={settings.startLabel} onChange={(e) => set('startLabel', e.target.value)} />
                    </Field>
                    <Field label="Input placeholder">
                      <TextInput value={settings.inputPlaceholder} onChange={(e) => set('inputPlaceholder', e.target.value)} />
                    </Field>
                  </div>
                  <Field label="Small print under the start button">
                    <TextInput value={settings.consentText} onChange={(e) => set('consentText', e.target.value)} />
                  </Field>

                  <div className="bg-muted/20 rounded-xl p-3.5 border border-border">
                    <Toggle checked={settings.poweredBy} onChange={() => toggle('poweredBy')} label="Show the “Powered by BrainCX” line" />
                  </div>
                </>
              )}

              {tab === 'behavior' && (
                <>
                  <div className="bg-muted/20 rounded-xl p-3.5 border border-border">
                    <ChatImageSharingSetting
                      enabled={imageSharing}
                      onChange={setImageSharing}
                      llmModel={agent?.llm_model}
                    />
                  </div>

                  <div className="space-y-3 bg-muted/20 rounded-xl p-3.5 border border-border">
                    <Toggle
                      checked={settings.hideAvatar}
                      onChange={() => toggle('hideAvatar')}
                      label="Run without the avatar"
                    />
                  </div>

                  <p className="text-[11px] text-muted-foreground leading-relaxed">
                    The conversation is unchanged: visitors still speak, still type, and still get the
                    transcript back. Only the video goes. The panel becomes a transcript from the header down,
                    no avatar is started for the call, and the assistant greets sooner because nothing is
                    waiting on a first video frame. If the start screen headline is still the stock
                    &ldquo;Talk face to face&rdquo;, it reads &ldquo;Ask us anything&rdquo; instead; a headline you
                    wrote yourself is left exactly as you wrote it.
                  </p>

                  {!settings.hideAvatar && (
                  <Field
                    label="Where the words go"
                    hint="Transcript is the full scrolling conversation. Subtitles keep only the latest line, over the video, for a video-first look."
                  >
                    <Segmented
                      value={settings.captionStyle}
                      onChange={(v) => set('captionStyle', v)}
                      options={[{ value: 'transcript', name: 'Transcript' }, { value: 'subtitles', name: 'Subtitles' }]}
                    />
                  </Field>
                  )}

                  <div className="space-y-3 bg-muted/20 rounded-xl p-3.5 border border-border">
                    <Toggle checked={settings.showTextInput} onChange={() => toggle('showTextInput')} label="Let visitors type as well as speak" />
                    <Toggle checked={settings.showMicToggle} onChange={() => toggle('showMicToggle')} label="Show the microphone mute button" />
                    <Toggle checked={settings.startMuted} onChange={() => toggle('startMuted')} label="Start with the microphone muted" />
                    <Toggle checked={settings.autoStart} onChange={() => toggle('autoStart')} label="Connect as soon as the panel opens" />
                    <Toggle checked={settings.showTimer} onChange={() => toggle('showTimer')} label="Show the session timer" />
                    <Toggle
                      checked={settings.minimizeEnabled}
                      onChange={() => toggle('minimizeEnabled')}
                      label="Allow minimizing to a pill (keeps the session running)"
                    />
                  </div>

                  <p className="text-[11px] text-muted-foreground leading-relaxed">
                    Minimizing never ends the call. The panel collapses to a pill
                    {settings.hideAvatar ? ' with the microphone still live' : ' with the live avatar still playing'}, and
                    reopening returns the visitor to the same conversation and transcript.
                  </p>

                  {!settings.hideAvatar && (
                  <div className="space-y-3 bg-muted/20 rounded-xl p-3.5 border border-border">
                    <Toggle
                      checked={settings.pipEnabled}
                      onChange={() => toggle('pipEnabled')}
                      label="Let visitors pop the video out and drag it around the page"
                    />
                    {settings.pipEnabled && (
                      <div className="grid grid-cols-2 gap-3">
                        <Field label="Card width">
                          <NumberInput
                            value={settings.pipWidth}
                            min={180}
                            max={520}
                            onChange={(v) => set('pipWidth', v)}
                            suffix="px"
                          />
                        </Field>
                        <Field label="Shape">
                          <Segmented
                            columns={3}
                            value={settings.pipAspect}
                            onChange={(v) => set('pipAspect', v)}
                            options={[
                              { value: 'landscape', name: '16:9' },
                              { value: 'portrait', name: '3:4' },
                              { value: 'square', name: '1:1' },
                            ]}
                          />
                        </Field>
                      </div>
                    )}
                  </div>
                  )}

                  {!settings.hideAvatar && (
                  <p className="text-[11px] text-muted-foreground leading-relaxed">
                    With pop out on, a picture in picture button appears on the video during a call. The video
                    detaches into a small card the visitor can drag anywhere on the page, with the microphone
                    still live and the conversation still running. Expanding puts it back in the panel with the
                    transcript intact.
                  </p>
                  )}

                  <div className="space-y-3 bg-muted/20 rounded-xl p-3.5 border border-border">
                    <Toggle
                      checked={settings.historyEnabled}
                      onChange={() => toggle('historyEnabled')}
                      label="Keep a 24 hour history visitors can resume"
                    />
                    {settings.historyEnabled && (
                      <Field label="History tab label">
                        <TextInput
                          value={settings.historyLabel}
                          onChange={(e) => set('historyLabel', e.target.value)}
                        />
                      </Field>
                    )}
                  </div>

                  <p className="text-[11px] text-muted-foreground leading-relaxed">
                    With History on, a clock appears in the panel header. Visitors see the conversations they
                    had on this site in the last 24 hours and can reopen any of them, and Continue restarts the
                    conversation with everything that was already said. Transcripts are held for 24 hours,
                    keyed to an anonymous per-browser token, and visitors can delete them from the panel.
                    History is not stored for agents running a PII policy of Mask or Drop.
                  </p>
                </>
              )}
            </div>
          </div>

          {/* Preview */}
          <div className="min-h-0 overflow-y-auto p-4 bg-muted/20 hidden lg:block">
            <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide mb-2">
              Preview
            </div>
            <Preview settings={previewSettings} theme={theme} />
            <p className="text-[11px] text-muted-foreground mt-3 leading-relaxed">
              The checkered area is the client's page showing through. The panel only ever covers its own
              rectangle, so visitors can keep reading and clicking while they talk.
            </p>
          </div>
        </div>

        <div className="border-t border-border p-4 bg-muted/30 flex justify-between items-center flex-shrink-0">
          <button
            type="button"
            onClick={handlePreview}
            className="px-4 py-2 bg-muted hover:bg-muted/80 text-foreground rounded-lg text-sm flex items-center gap-2"
          >
            <Eye className="w-4 h-4" />
            Open live preview
          </button>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="px-4 py-2 bg-muted hover:bg-muted/80 rounded-lg text-sm">
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={isSaving}
              className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg text-sm disabled:opacity-50"
            >
              {isSaving ? 'Saving…' : 'Save settings'}
            </button>
          </div>
        </div>
      </div>
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
};

export default AvatarPanelThemeCustomizer;
